/**
 * Presentational model types shared by the prediction "tile" UI components
 * (`@kikkz/shared/prediction-game/ui`). Kept deliberately small and
 * brand-neutral so any client (b04, koeln, …) can reuse the tiles. The richer,
 * client-specific models (Fixture, RankingEntry, GameScoreValues, …) stay in
 * each client's `prediction-game/util`.
 */

export interface Team {
  name: string;
  imageUrl: string;
}

export type PlayerPosition = 'goalkeeper' | 'defender' | 'midfielder' | 'attacker';

export interface Player {
  id: string;
  firstName: string;
  lastName: string;
  /** Display name (backend `displayName`). */
  name: string;
  imageUrl: string;
  /** Celebration variant of the player image (backend `imageUrlLargeCelebrating`). */
  imageUrlCelebrating: string;
  position: PlayerPosition;
  isActive: boolean;
  // Per-player scoring is not (yet) provided by the backend — placeholder, see gap analysis.
  scoreLineup: number;
  scoreFirstGoalScorer: number;
}

/** Squad players offered in pickers (lineup, first goal scorer, season questions):
 *  only players still in the team. An empty filter result (e.g. the backend does not
 *  maintain the flag) falls back to the full squad so pickers never come up empty. */
export function selectablePlayers(players: Player[]): Player[] {
  const active = players.filter((player) => player.isActive);
  return active.length > 0 ? active : players;
}

export type FixtureStatus = 'SCHEDULED' | 'LIVE' | 'FINISHED';
